import { createSelector } from '@reduxjs/toolkit';
import { SetupIntent, Token } from '@stripe/stripe-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useContext } from 'react';
import Stripe from 'stripe';

import PaymentsClient from 'src/clients/PaymentsClient';
import { BankVerifyFormModel } from 'src/constants/componentTypes';
import { PortalConfigContext } from 'src/context';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useInvoiceRecipient } from 'src/legacy/components/billing/new/useInvoiceRecipient';
import { FormModal } from 'src/legacy/components/Modals';
import { useAddBankForm } from 'src/legacy/components/PaymentMethodForm/useAddBankForm';
import { useAddCreditCard } from 'src/legacy/components/PaymentMethodForm/useAddCreditCard';
import { AddPaymentMethodModal } from 'src/routes/billing/invoices/components/InvoiceEditPage/AddPaymentMethodModal';
import { PaymentMethodType } from 'src/routes/billing/invoices/components/InvoiceEditPage/ChoosePaymentMethod';
import {
  useCreateBankSetupIntentMutation,
  useCreateUnverifiedBankAccountMutation,
  useDeletePaymentMethodMutation,
  useDeleteUnverifiedBankAccountMutation,
  useGetCustomerBankAccountsQuery,
  useGetCustomerPaymentMethodsQuery,
  useGetStripeCustomerQuery,
  useGetUnverifiedBankAccountsQuery,
  useSetDefaultPaymentMethodMutation,
  useVerifyUnverifiedBankAccountMutation,
} from 'src/services/api';
import { PaymentOptions, RecipientType } from 'src/services/api/invoicesApi';
import { AddPaymentMethod, updateSource } from 'src/store/payments/actions';
import { RootState } from 'src/store/reduxTypes';
import { StripePaymentMethod } from 'src/types/payment';
import { arrayToMap } from 'src/utils/array';

// Stable empty array to prevent re-renders when feature flag is off
const EMPTY_UNVERIFIED_BANK_ACCOUNTS: never[] = [];

export const defaultPaymentOptions: PaymentOptions = {
  allowPaymentViaACH: true,
  allowPaymentViaCC: true,
  absorbACHFees: false,
  absorbCreditCardFee: false,
  allowPlaidSetup: true,
};

export const initialValues = {
  sourceId: '',
  isPrimary: false,
  disabledCheckPrimary: false,
};

export const initialCardFormValues = {
  ...initialValues,
  name: '',
  postalCode: '',
};

export const initialBankFormValues = {
  ...initialValues,
  accountHolderType: 'individual',
  accountNumber: '',
  accountHolderName: '',
  routingNumber: '',
};

interface UsePaymentMethodsOptions {
  recipientId: string;
  paymentOptions?: PaymentOptions;
  amountDue?: number;
  currency?: string;
  companyId?: string;
}
export type HandleSaveValues = {
  sourceId?: string;
  isPrimary: boolean;
  name?: string;
  postalCode?: string;
  fields?: {
    token?: Token;
    setupIntent?: SetupIntent;
  };
};

const selector = createSelector(
  [
    (state: RootState) => state.user.id,
    (state: RootState) => state.user.instance?.attributes.email,
    (state: RootState) => state.user.data?.portalId,
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.activeCompany,
  ],
  (userId, currentUserEmail, portalId, activeClients, activeCompany) => ({
    userId,
    currentUserEmail,
    portalId,
    activeClients: arrayToMap(activeClients, 'id'),
    activeCompanyId: activeCompany.activeCompanyId ?? '',
  }),
);
type InitialValuesType = {
  sourceId: string;
  isPrimary: boolean;
  disabledCheckPrimary: boolean;
};
export type SetEditModalData = {
  values: InitialValuesType;
  paymentMethodType: PaymentMethodType;
  open: boolean;
};

export const usePaymentMethods = (options: UsePaymentMethodsOptions) => {
  const { recipientId, paymentOptions = defaultPaymentOptions } = options;
  const enableMicrodepositVerification = useFeatureFlagEnabled(
    'enable-microdeposit-verification',
  );
  const invoiceRecipient = useInvoiceRecipient(
    recipientId,
    options?.companyId || '',
  );
  const isRecipientACompany = invoiceRecipient?.type === RecipientType.Company;
  const portalConfig = useContext(PortalConfigContext);
  const portalId = portalConfig.portalHeader;
  const { userId, currentUserEmail, activeClients, activeCompanyId } =
    useAppSelector(selector);
  const companyId =
    options?.companyId ?? (isRecipientACompany ? recipientId : activeCompanyId);

  const { data: stripeCustomer, refetch: loadCustomer } =
    useGetStripeCustomerQuery(
      {
        companyId,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !companyId,
      },
    );
  const stripeCustomerId = stripeCustomer?.id || '';
  // TODO(@Monfernape): refactor, seems like we can get rid of this
  const {
    data: paymentMethods = [],
    isLoading: isPaymentMethodLoading,
    refetch: reloadPaymentMethods,
  } = useGetCustomerPaymentMethodsQuery(stripeCustomerId, {
    refetchOnMountOrArgChange: true,
  });
  // TODO(@Monfernape): refactor, seems like we can get rid of this
  const {
    data: bankAccountsData,
    isLoading: isBankAccountsLoading,
    refetch: loadBankAccounts,
  } = useGetCustomerBankAccountsQuery(stripeCustomerId, {
    skip: !stripeCustomerId,
    refetchOnMountOrArgChange: true,
  });

  const legacyBankAccounts = bankAccountsData || [];

  // Fetch unverified bank accounts (awaiting microdeposit verification)
  // Only fetch if the feature flag is enabled
  const {
    data: unverifiedBankAccounts = [],
    refetch: loadUnverifiedBankAccounts,
  } = useGetUnverifiedBankAccountsQuery(companyId, {
    skip: !companyId || !enableMicrodepositVerification,
    refetchOnMountOrArgChange: true,
  });

  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const [deletePaymentMethod] = useDeletePaymentMethodMutation();
  const [createBankSetupIntent] = useCreateBankSetupIntentMutation();
  const [createUnverifiedBankAccount] =
    useCreateUnverifiedBankAccountMutation();
  const [verifyUnverifiedBankAccount] =
    useVerifyUnverifiedBankAccountMutation();
  const [deleteUnverifiedBankAccount] =
    useDeleteUnverifiedBankAccountMutation();
  const dispatch = useAppDispatch();
  const [showAddPaymentModal, setShowAddPaymentModal] = React.useState(false);
  const [sources, setSources] = React.useState<StripePaymentMethod[]>([]);
  const [currentUpdatingSourceId, setCurrentUpdatingSourceId] =
    React.useState('');
  const [showBankAddedModal, setShowBankAddedModal] =
    React.useState<Stripe.BankAccount | null>(null);
  const [bankSetupIntentClientSecret, setBankSetupIntentClientSecret] =
    React.useState<string | undefined>(undefined);
  const [editModalData, setEditModalData] = React.useState({
    values: initialValues,
    paymentMethodType: '',
    open: false,
  });

  const recipient = React.useMemo(() => {
    if (isRecipientACompany) {
      const companyClientUser = Array.from(activeClients.values()).find(
        (c) =>
          c.fields.companyId === recipientId ||
          c.fields.companyIds?.includes(recipientId),
      );
      if (companyClientUser) {
        return companyClientUser;
      }
    } else {
      const fetchedClient = activeClients.get(recipientId);
      if (fetchedClient) {
        return fetchedClient;
      }
    }
    return null;
  }, [recipientId, activeClients, isRecipientACompany]);

  const defaultPaymentMethod = React.useMemo(() => {
    if (!stripeCustomer) {
      return '';
    }

    // stripe by default supports backwards compatibility of payment method with payment source
    // but it keeps default payment source and default payment method in separate fields
    // i.e. default payment source = customer.default_source
    // and default payment method = customer.invoice_settings.default_payment_method
    // so logic below handles the selection of default value from both fields
    const isPaymentMethod = (
      paymentMethod: string | Stripe.PaymentMethod,
    ): paymentMethod is Stripe.PaymentMethod =>
      typeof paymentMethod !== 'string';

    const isPaymentSource = (
      source: string | Stripe.CustomerSource | null,
    ): source is string => typeof source === 'string';

    const getPaymentSource = (source: string | Stripe.CustomerSource | null) =>
      isPaymentSource(source) ? source : '';

    return stripeCustomer.invoice_settings.default_payment_method &&
      isPaymentMethod(stripeCustomer.invoice_settings.default_payment_method)
      ? stripeCustomer.invoice_settings.default_payment_method.id
      : getPaymentSource(stripeCustomer.default_source);
  }, [stripeCustomer]);

  /**
   * if user edits existing source, update source
   * if user creates a new source, add source to the customer
   * @param values the fields of add/edit payment method form modal
   */

  const handleSave = async (values: HandleSaveValues) => {
    if (values.sourceId) {
      setCurrentUpdatingSourceId(values.sourceId);
      dispatch(
        updateSource(
          stripeCustomerId,
          values.sourceId,
          {
            name: values.name,
            address_zip: values.postalCode,
          },
          values.isPrimary,
        ),
      );
      setCurrentUpdatingSourceId('');
    } else if (values.fields?.setupIntent) {
      // SetupIntent confirmed - bank account pending microdeposit verification
      // Note: us_bank_account PaymentMethods cannot be attached to a customer
      // until they are verified via microdeposits. The PaymentMethod will be
      // stored in pending_bank_accounts table and attached via webhook after verification.
      const paymentMethodId = values.fields.setupIntent
        .payment_method as string;
      const setupIntentId = values.fields.setupIntent.id;

      // Store the unverified bank account in the database for display
      if (companyId && paymentMethodId && setupIntentId) {
        try {
          await createUnverifiedBankAccount({
            companyId,
            setupIntentId,
            paymentMethodId,
          }).unwrap();
        } catch (err) {
          console.error('Failed to create unverified bank account:', err);
        }
      }

      if (editModalData.paymentMethodType === 'bank') {
        // Create a minimal bank account object for the modal
        // The actual bank account details will be fetched on refresh
        setShowBankAddedModal({
          id: paymentMethodId,
          object: 'bank_account',
          status: 'new',
        } as Stripe.BankAccount);
      }
      // Clear the client secret, then close the modal after successful setup
      setBankSetupIntentClientSecret(undefined);
      setEditModalData({
        values: initialValues,
        open: false,
        paymentMethodType: '',
      });
    } else {
      // Credit card flow: create PaymentMethod from token
      const email = recipient ? recipient?.fields.email : currentUserEmail;

      await dispatch(
        AddPaymentMethod(
          recipient?.id || userId,
          email || '',
          values.fields?.token?.id ?? '',
          stripeCustomerId,
          values.isPrimary,
          portalId,
          companyId,
        ),
      );
    }

    await loadCustomer();
    await reloadPaymentMethods();
    await loadBankAccounts();
    await loadUnverifiedBankAccounts();
  };

  /**
   * verify the microdeposit amounts for a bank source
   */
  const handleVerify = async (values: BankVerifyFormModel) => {
    const verifyResult = await PaymentsClient.verifyBank(
      stripeCustomerId,
      values.id,
      {
        amounts: [Number(values.fromAmount), Number(values.toAmount)],
      },
    );
    if (verifyResult && verifyResult.data) {
      setSources(
        sources
          .filter((sourceItem) => sourceItem.id !== verifyResult.data.id)
          .concat([verifyResult.data]),
      );

      loadBankAccounts();
    } else {
      throw new Error('Invalid deposite numbers. Cannot be verified');
    }
  };

  /**
   * verify the microdeposit amounts for an unverified bank account (SetupIntent flow)
   */
  const handleVerifyUnverifiedBankAccount = async (
    values: BankVerifyFormModel,
  ) => {
    await verifyUnverifiedBankAccount({
      pendingBankAccountId: values.id,
      amounts: [Number(values.fromAmount), Number(values.toAmount)],
    }).unwrap();

    // Refresh all data after successful verification
    await loadCustomer();
    await reloadPaymentMethods();
    await loadBankAccounts();
    await loadUnverifiedBankAccounts();
  };

  /**
   * Delete an unverified bank account
   */
  const handleDeleteUnverifiedBankAccount = async (
    unverifiedBankAccountId: string,
  ) => {
    try {
      await deleteUnverifiedBankAccount(unverifiedBankAccountId).unwrap();
      await loadUnverifiedBankAccounts();
    } catch (err) {
      console.error('Failed to delete unverified bank account:', err);
      throw err;
    }
  };

  /**
   * Update only isPrimary field of the current payment method to be true, while using the other fields as it is.
   * To update isPrimary field of the current source, use the same function as the one to create/edit payment method
   * @param source card information.
   */
  const makePrimaryMethod = (source: StripePaymentMethod) => {
    setDefaultPaymentMethod({
      defaultPaymentMethod: source.id,
      companyId,
    });
  };

  /**
   * Delete payment method
   * set the current updating source id to render loading state UI of current payment method row while deleting.
   * Once the payment method is deleted, set the current payment source id to empty string to stop rendering loading state.
   * After deleting the payment source, it loads the updated list of payment sources of the current customer.
   * @param sourceId
   */
  const handleDelete = async (sourceId: string) => {
    deletePaymentMethod({
      paymentMethodId: sourceId,
      companyId,
    });
  };

  const handleClose = () => {
    setEditModalData({
      values: initialValues,
      open: false,
      paymentMethodType: '',
    });
    // Clear any pending SetupIntent client secret
    setBankSetupIntentClientSecret(undefined);
  };

  const openAddPaymentModal = () => {
    setShowAddPaymentModal(true);
  };

  const hideAddPaymentModal = () => {
    setShowAddPaymentModal(false);
  };

  const handleAddPaymentMethodSelected = async (
    paymentMethodType: PaymentMethodType,
    newSource?: StripePaymentMethod,
  ) => {
    if (paymentMethodType !== 'plaid') {
      // set the existing payment methods to determine if the newly added
      // payment should be the primary one.
      const existingPaymentMethods = [
        ...(paymentMethods || []),
        ...legacyBankAccounts,
      ];

      // For bank accounts with microdeposit verification enabled,
      // fetch SetupIntent client secret first. SetupIntent uses on_behalf_of
      // so microdeposits show the portal's business name
      if (
        paymentMethodType === 'bank' &&
        companyId &&
        enableMicrodepositVerification
      ) {
        const setupIntent = await createBankSetupIntent().unwrap();
        setBankSetupIntentClientSecret(setupIntent.clientSecret);
      }

      setEditModalData({
        open: true,
        paymentMethodType,
        values: {
          ...(paymentMethodType === 'bank'
            ? initialBankFormValues
            : initialCardFormValues),
          isPrimary:
            paymentMethodType === 'bank'
              ? existingPaymentMethods.length === 0
              : initialCardFormValues.isPrimary,
          disabledCheckPrimary:
            paymentMethods.length === 0 ||
            defaultPaymentMethod === currentUpdatingSourceId,
        },
      });
    } else if (newSource) {
      const currentURL = window.location.href;
      const urlParts = currentURL.split('?');
      const currentURLWithoutQuery = urlParts.at(0);
      const currentQuery = urlParts.at(1) || '';
      // when a new source is added and the flow was completed using the plaid oauth
      // flow there will be a oauth redicert query param which should be removed
      // otherwise the oauth flow will re-trigger
      if (currentQuery.indexOf('plaidOauthRedirectURL') > -1) {
        const filteredQueryParams = currentQuery
          .split('&')
          .filter(
            (queryParam) =>
              !queryParam.includes('plaidOauthRedirectURL') &&
              !queryParam.includes('linkToken'),
          );
        window.location.href = `${currentURLWithoutQuery}?${filteredQueryParams.join(
          '&',
        )}`;
      }
      loadCustomer();
    }
    hideAddPaymentModal();
  };

  /**
   * editModalRenderer returns the modal component to render when user clicks to edit
   * a payment method which sets modal data open = true. When not open ~= false, the
   * add payment method modal is rendered and the open/close state for that is controlled
   * in state separately.
   */

  const editModalRenderer = () => {
    const { open, values, paymentMethodType } = editModalData;

    const sourceId = values.sourceId || '';
    const paymentMethodDetails = paymentMethods.find(
      (method) => method.id === sourceId,
    );
    const isDefaultPaymentMethod =
      paymentMethodDetails?.id === defaultPaymentMethod;

    const formValues = sourceId
      ? {
          ...values,
          name: paymentMethodDetails?.billing_details.name || '',
          postalCode:
            paymentMethodDetails?.billing_details.address?.postal_code || '',
          cardEnding: paymentMethodDetails?.card?.last4 || '',
          brand: paymentMethodDetails?.card?.brand || '',
          isPrimary: isDefaultPaymentMethod || false,
          expMonth: paymentMethodDetails?.card?.exp_month || '',
          expYear: paymentMethodDetails?.card?.exp_year || '',
          isDefaultPaymentMethod: isDefaultPaymentMethod,
        }
      : values;

    if (!open) {
      return (
        <AddPaymentMethodModal
          modalProps={{
            open: showAddPaymentModal,
            onClose: hideAddPaymentModal,
          }}
          paymentOptions={paymentOptions}
          onPaymentMethodSelected={handleAddPaymentMethodSelected}
          amountDue={options.amountDue || 0}
          currency={options.currency || ''}
        />
      );
    }

    const formModalOptions = {
      title: '',
      initialFormValue: formValues,
      buttonLabel: values.sourceId ? 'Update' : 'Add',
      handleSave,
      onClose: handleClose,
      open,
    };

    const titlePrefix = values.sourceId ? 'Update' : 'Add';
    formModalOptions.title =
      paymentMethodType === 'credit_card'
        ? `${titlePrefix} card`
        : `${titlePrefix} bank account`;

    return (
      <FormModal
        {...formModalOptions}
        useFormHook={() =>
          paymentMethodType === 'credit_card'
            ? useAddCreditCard({ paymentMethods })
            : useAddBankForm({
                clientSecret: bankSetupIntentClientSecret,
                enableMicrodepositVerification:
                  enableMicrodepositVerification ?? false,
              })
        }
      />
    );
  };

  // Use our managed sources state instead of direct query result
  // Only return card PaymentMethods here - bank accounts are handled separately
  const currentSources = React.useMemo(() => {
    // If no recipient or no stripe customer, return empty
    if (!stripeCustomerId || (!recipient && !companyId)) {
      return [];
    }
    return paymentMethods;
  }, [stripeCustomerId, paymentMethods, recipient, companyId]);

  return {
    sources: currentSources,
    bankAccounts: legacyBankAccounts.reduce(
      (acc, bankAccount) => {
        acc[bankAccount.id] = bankAccount;
        return acc;
      },
      {} as Record<string, Stripe.BankAccount>,
    ),
    // Unverified bank accounts awaiting microdeposit verification
    // Only available when enableMicrodepositVerification flag is enabled
    unverifiedBankAccounts: enableMicrodepositVerification
      ? unverifiedBankAccounts
      : EMPTY_UNVERIFIED_BANK_ACCOUNTS,
    defaultSource: defaultPaymentMethod,
    stripeCustomerId,
    makePrimaryMethod,
    currentUpdatingSourceId,
    editModalData,
    setEditModalData,
    handleSave,
    handleDelete,
    handleClose,
    editModalRenderer,
    isSourcesLoading: isPaymentMethodLoading,
    isBankAccountsLoading,
    handleVerify,
    // Only provide unverified bank account handlers when feature flag is enabled
    handleVerifyUnverifiedBankAccount: enableMicrodepositVerification
      ? handleVerifyUnverifiedBankAccount
      : undefined,
    handleDeleteUnverifiedBankAccount: enableMicrodepositVerification
      ? handleDeleteUnverifiedBankAccount
      : undefined,
    showBankAddedModal,
    setShowBankAddedModal,
    openAddPaymentModal,
    handleAddPaymentMethodSelected,
    enableMicrodepositVerification,
  } as const;
};
